import React from "react";
import { Activity, Power, Radio, ShieldCheck, Zap, LogOut, Siren, Menu } from "lucide-react";

export default function Header({ snapshot, onToggleLive, onKill, onNewSignal, onPanic, onMenu, user, onLogout, onAccountMode }) {
  const bridge = snapshot?.bridge || {};
  const live = snapshot?.liveEnabled;
  const connected = bridge.connected;
  const accountMode = snapshot?.accountMode || "demo";
  const canTrade = user && user.role !== "readonly";

  return (
    <header data-testid="mt5-command-header"
      className="sticky top-0 z-40 bg-[#0E1422]/95 backdrop-blur-md border-b border-white/10">
      <div className="h-1 w-full bg-gradient-to-r from-amber-400 via-rose-500 via-40% to-sky-400" />
      <div className="min-h-16 px-3 md:px-6 py-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          <button data-testid="mobile-nav-toggle" onClick={onMenu}
            className="lg:hidden w-9 h-9 rounded-lg grid place-items-center text-slate-300 bg-[#0F1728] border border-white/10 hover:text-slate-100 shrink-0" aria-label="Menu sezioni">
            <Menu size={18} />
          </button>
          <div className="w-10 h-10 rounded-xl grid place-items-center bg-gradient-to-br from-amber-400 to-rose-500 text-[#0B0E17] font-black shadow-[0_0_24px_rgba(245,158,11,0.4)] shrink-0">
            CAV
          </div>
          <div className="min-w-0">
            <div className="font-heading font-extrabold tracking-tight text-[15px] leading-none truncate">
              <span className="gold-text">C.A.V</span> · Condannati a Vincere
            </div>
            <div className="text-[10px] text-slate-400 mt-1 truncate">MT5 XAUUSD Command Center</div>
          </div>
        </div>

        <div className="hidden xl:flex items-center gap-2">
          <StatusChip testid="mt5-heartbeat-ping" icon={<Activity size={13} />} ok={connected}
            label={connected ? `HEARTBEAT ${bridge.latencyMs || 14}ms` : "NESSUN HEARTBEAT"} ping />
          <StatusChip testid="mt5-bridge-status-badge" icon={<Radio size={13} />} ok={connected}
            label={connected ? "BRIDGE: ONLINE" : "BRIDGE: OFFLINE"} />
          <span className="text-[10px] font-mono font-bold tracking-wide px-2.5 py-1.5 rounded-md bg-amber-500/12 text-amber-400 border border-amber-500/25">
            XAUUSD
          </span>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end">
          <div className="flex bg-[#0F1728] border border-white/10 p-1 rounded-lg" data-testid="account-mode-switch">
            {["demo", "real"].map((m) => (
              <button key={m} data-testid={`account-mode-${m}`} disabled={!canTrade} onClick={() => onAccountMode(m)}
                className={`px-2.5 py-1.5 rounded-md text-[10px] font-bold uppercase transition-colors disabled:opacity-40 ${
                  accountMode === m ? (m === "real" ? "bg-rose-500/80 text-white" : "bg-sky-500/80 text-white") : "text-slate-400 hover:text-slate-100"}`}>
                {m === "real" ? "Reale" : "Demo"}
              </button>
            ))}
          </div>

          <button data-testid="live-mode-toggle-switch" disabled={!canTrade} onClick={() => onToggleLive(!live)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold border transition-colors disabled:opacity-40 ${
              live ? "bg-amber-500 text-[#0B0E17] border-amber-400" : "text-slate-300 border-white/10 bg-[#0F1728] hover:text-slate-100"}`}>
            <ShieldCheck size={13} /> {live ? "LIVE" : "DEMO/TEST"}
          </button>

          {canTrade && (
            <button data-testid="panic-button" onClick={onPanic}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-black text-white bg-gradient-to-br from-rose-600 to-red-700 border border-rose-400/40 hover:from-rose-500 hover:to-red-600 transition-colors shadow-[0_0_22px_rgba(244,63,94,0.5)] led-ping"
              title="PANIC: chiude tutte le posizioni e disattiva il Live">
              <Siren size={14} /> PANIC
            </button>
          )}

          {canTrade && (
            <button data-testid="emergency-kill-switch-button" onClick={onKill}
              className="hidden md:flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold text-rose-300 bg-rose-500/10 border border-rose-500/25 hover:bg-rose-500/20 transition-colors" title="Kill switch">
              <Power size={13} /> KILL
            </button>
          )}

          {canTrade && (
            <button data-testid="open-add-signal-dialog-button-header" onClick={onNewSignal}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-bold text-[#0B0E17] bg-gradient-to-br from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 transition-colors shadow-[0_0_20px_rgba(245,158,11,0.25)]">
              <Zap size={13} /> <span className="hidden sm:inline">NUOVO</span> SEGNALE
            </button>
          )}

          <div className="flex items-center gap-2 pl-1.5 border-l border-white/10 ml-0.5">
            <div className="hidden lg:block text-right leading-tight">
              <div className="text-[11px] text-slate-200">{user?.name}</div>
              <div className="text-[9px] text-amber-400 uppercase font-mono">{user?.role}</div>
            </div>
            <button data-testid="logout-button" onClick={onLogout}
              className="w-9 h-9 rounded-lg grid place-items-center text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors" title="Esci">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

function StatusChip({ icon, ok, label, ping, testid }) {
  return (
    <span data-testid={testid}
      className={`flex items-center gap-1.5 text-[10px] font-mono font-bold tracking-wide px-2.5 py-1.5 rounded-md border ${
        ok ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/25" : "bg-rose-500/10 text-rose-400 border-rose-500/25"}`}>
      <span className={`w-2 h-2 rounded-full ${ok ? "bg-emerald-400" : "bg-rose-400"} ${ping && ok ? "led-ping" : ""}`} />
      {icon}{label}
    </span>
  );
}
